import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import type { DataTableQueryParams } from '~/components/DataTable/DataTable'
import type { FiltersState } from '~/components/Filters/FilterBar/types'
import { ToastType } from '~/components/Global/Interactions/Toast/ToastType'
import { useFetchCompanyContactIds } from '~/domains/companies/queries/companyContactIds'
import type { CompanyListItem } from '~/domains/companies/types'
import { useConfig } from '~/domains/configuration/queries/config'
import { toast } from '~/utils'

type ContactSelection = ReadonlyMap<number, number>

const notifySelectionError = () =>
  toast('Impossible de sélectionner les contacts', '', ToastType.Danger)

const notifySelectionLimit = (limit: number) =>
  toast(
    `La sélection est limitée à ${limit} contacts, affinez vos filtres`,
    '',
    ToastType.Warning
  )

export const useCompanyContactSelection = (
  barFilters: FiltersState,
  columnFilters: DataTableQueryParams['columnFilters']
) => {
  const fetchContactIds = useFetchCompanyContactIds()
  const { data: selectionLimit } = useConfig('actions_limit.selection')
  const [selection, setSelection] = useState<ContactSelection>(new Map())
  const [isAllSelected, setIsAllSelected] = useState(false)
  const [isSelectingAll, setIsSelectingAll] = useState(false)
  const selectAllRequest = useRef(0)
  const currentScope = useRef('')

  const filtersScope = JSON.stringify([barFilters, columnFilters])
  const [selectionScope, setSelectionScope] = useState(filtersScope)
  if (selectionScope !== filtersScope) {
    setSelectionScope(filtersScope)
    setSelection(new Map())
    setIsAllSelected(false)
  }

  useEffect(() => {
    currentScope.current = filtersScope
  }, [filtersScope])

  const isWithinLimit = useCallback(
    (size: number) => {
      if (selectionLimit === undefined || size <= selectionLimit) {
        return true
      }
      notifySelectionLimit(selectionLimit)
      return false
    },
    [selectionLimit]
  )

  const replaceSelection = useCallback((next: ContactSelection) => {
    selectAllRequest.current += 1
    setIsSelectingAll(false)
    setIsAllSelected(false)
    setSelection(next)
  }, [])

  const selectedContactIds = useMemo(() => [...selection.keys()], [selection])

  const selectedCountByCompany = useMemo(() => {
    const counts = new Map<number, number>()
    for (const companyId of selection.values()) {
      counts.set(companyId, (counts.get(companyId) ?? 0) + 1)
    }
    return counts
  }, [selection])

  const toggleContact = useCallback(
    (contactId: number, companyId: number) => {
      const next = new Map(selection)
      if (next.has(contactId)) {
        next.delete(contactId)
      } else {
        next.set(contactId, companyId)
      }
      if (isWithinLimit(next.size)) {
        replaceSelection(next)
      }
    },
    [selection, isWithinLimit, replaceSelection]
  )

  const removeContact = useCallback(
    (contactId: number) => {
      const next = new Map(selection)
      next.delete(contactId)
      replaceSelection(next)
    },
    [selection, replaceSelection]
  )

  const selectCompanies = useCallback(
    (companies: CompanyListItem[]) => {
      const next = new Map(selection)
      for (const company of companies) {
        for (const contactId of company.matchingContactIds) {
          next.set(contactId, company.id)
        }
      }
      if (isWithinLimit(next.size)) {
        replaceSelection(next)
      }
    },
    [selection, isWithinLimit, replaceSelection]
  )

  const unselectCompanies = useCallback(
    (companyIds: number[]) => {
      replaceSelection(
        new Map(
          [...selection].filter(
            ([, companyId]) => !companyIds.includes(companyId)
          )
        )
      )
    },
    [selection, replaceSelection]
  )

  const selectAll = useCallback(
    async (search: string) => {
      selectAllRequest.current += 1
      const request = selectAllRequest.current
      const scope = currentScope.current
      const isStale = () =>
        request !== selectAllRequest.current || scope !== currentScope.current
      setIsSelectingAll(true)
      try {
        const contacts = await fetchContactIds(search)
        if (isStale() || !isWithinLimit(contacts.length)) {
          return
        }
        setSelection(
          new Map(contacts.map(({ id, companyId }) => [id, companyId]))
        )
        setIsAllSelected(true)
      } catch {
        if (!isStale()) {
          notifySelectionError()
        }
      } finally {
        if (request === selectAllRequest.current) {
          setIsSelectingAll(false)
        }
      }
    },
    [fetchContactIds, isWithinLimit]
  )

  const clear = useCallback(() => {
    replaceSelection(new Map())
  }, [replaceSelection])

  return {
    selectedContactIds,
    selectedCountByCompany,
    isAllSelected,
    isSelectingAll,
    toggleContact,
    removeContact,
    selectCompanies,
    unselectCompanies,
    selectAll,
    clear
  }
}
